import { Suspense, type ReactElement } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { routePaths } from '@/app/route-paths';
import { Button } from '@/components/ui/button';
import { useLogout } from '@/features/auth/auth-mutations';
import { useCurrentUser } from '@/features/auth/auth-queries';
import { cn } from '@/lib/cn';

const navItems = [{ to: routePaths.dashboard, label: 'Overview' }] as const;

export function AppShell(): ReactElement {
  const user = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh bg-surface">
      <header className="border-b border-line bg-surface-raised">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-gutter">
          <div className="flex items-center gap-6">
            <span className="text-base font-semibold tracking-tight">iDAL</span>
            <nav className="flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'rounded-control px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-fg-muted hover:bg-surface-sunken',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-fg-muted sm:inline">{user?.businessName}</span>
            <Button
              variant="ghost"
              isLoading={logout.isPending}
              onClick={() => {
                logout.mutate(undefined, {
                  onSettled: () => {
                    // The front door (ADR-0031), and the same place RequireAuth's
                    // redirect already races to the moment the session clears.
                    void navigate(routePaths.phone, { replace: true });
                  },
                });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl p-gutter">
        <Suspense fallback={<p className="text-caption text-fg-muted">Loading</p>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
